import styled, { css } from 'styled-components';
import BigNumber from 'bignumber.js';
import { useMemo } from 'react';
import {
  useEstimatedPriceModalMethods,
  useEstimatedPriceModalState,
} from '@paradigm/features/src/unified-rfqs/ui/drfq/quote-builder/estimated-price-modal-context';
import HeadedModal from '@paradigm/design-system/src/components/HeadedModal';
import { useDrfqContext } from '@paradigm/features/src/unified-rfqs/ui/rfq-context';
import Button from '@paradigm/design-system/src/components/Button';
import { useQuoteBuilderView } from '@paradigm/features/src/unified-rfqs/ui/drfq/quote-builder/view';
import {
  cloudBurst,
  darkerBlackPearl,
  paradigmLightBlue,
} from '@paradigm/design-system/src/colors';
import WarningIcon from '@paradigm/design-system/src/assets/WarningIcon';
import ArrowDownIcon from '@paradigm/design-system/src/assets/ArrowDownIcon';
import { useQuoteBuilderModalMethods } from '@paradigm/features/src/unified-rfqs/ui/drfq/quote-builder/modal-context';
import { usePriceFormatterFn } from '@paradigm/features/src/unified-rfqs/ui/hooks/useFormattedPrice';
import { findBaseProductCode } from '@paradigm/features/src/products/domain';
import { Maybe } from '@paradigm/utils/types';
import { Side } from '@paradigm/features/src/unified-rfqs/entities/shared';
import { unformatNegativeSign } from '@paradigm/utils/src/enhanceNegativeSign';
import CheckCircleIcon from '@paradigm/design-system/src/assets/CheckCircleIcon';
import { roundPrecisionBySide } from '@paradigm/features/src/unified-rfqs/utils/rfq-price-functions';
import { toast } from '#/components/UnifiedRfqs/toast';
import MarkPrice from '#/components/UnifiedRfqs/components/MarkPrice';
import { MakerBadgeContainer } from './atoms';
import MakerBadge from './MakerBadge';
import TwoWayInput from './TwoWayInput';

export default function EstimatedPriceModal() {
  const { isOpen, estimatedPrices } = useEstimatedPriceModalState();
  const { close, submit, editLegPrices } = useEstimatedPriceModalMethods();
  const quoteModal = useQuoteBuilderModalMethods();
  const { rfq: drfq } = useDrfqContext();
  const { price: twoWayPrice } = useQuoteBuilderView();
  const [bidPrice, askPrice] = twoWayPrice.value;
  const formatPriceBase = usePriceFormatterFn({
    productCode: findBaseProductCode(drfq?.product_codes ?? []),
    rfqType: 'drfq',
    side: null,
    description: drfq?.description,
  });
  const formatPrice = (price: Maybe<string>, side: Side) => {
    const formatted = formatPriceBase(price, side);
    const cleaned = unformatNegativeSign(formatted ?? '');
    return cleaned;
  };

  const estimatedBidRounded = formatPrice(
    estimatedPrices?.total_bid_price,
    'BUY',
  );
  const estimatedAskRounded = formatPrice(
    estimatedPrices?.total_ask_price,
    'SELL',
  );

  const handleSubmitQuote = () => {
    submit(({ errors }) => {
      if (errors.length > 0) {
        errors.forEach((error) => toast(error));
        return;
      }
      close();
      quoteModal.close();
    });
  };

  const [bidPriceDiff, askPriceDiff] = useMemo(() => {
    const { precision } = twoWayPrice;
    const bidDiff = BigNumber(bidPrice).minus(estimatedBidRounded).negated();
    const askDiff = BigNumber(askPrice).minus(estimatedAskRounded).negated();
    const bidDiffFormatted = formatPriceDiff(bidDiff, 'BUY', precision);
    const askDiffFormatted = formatPriceDiff(askDiff, 'SELL', precision);

    return [bidDiffFormatted, askDiffFormatted];
  }, [
    twoWayPrice,
    bidPrice,
    askPrice,
    estimatedBidRounded,
    estimatedAskRounded,
  ]);

  return (
    <HeadedModal
      title={
        <div css="display: flex; align-items: center; gap: 8px;">
          <WarningIcon width={20} height={20} /> Invalid Structure Price
        </div>
      }
      isOpen={isOpen}
      titleCss={css`
        white-space: nowrap;
      `}
      close={close}
      rightInfo={
        <MakerBadgeContainer>
          <MakerBadge />
        </MakerBadgeContainer>
      }
      fade={false}
      width={509}
      aria-label="drfq quote builder modal"
    >
      <Body>
        <WarningContainer>
          <div css="font-size: 14px;margin-bottom: 8px; color: white;">
            The price you entered results in invalid leg prices.{' '}
          </div>
          To proceed, either:
          <NumberedList css="line-height: 1.5;">
            <li>Accept Nearest Valid Price OR</li>
            <li>
              Reject and Edit Leg Prices manually using Mark Prices as a
              baseline.
            </li>
          </NumberedList>
        </WarningContainer>
        <DarkBlock>
          <MarkPrice />
          <PriceContainer>
            <WarningIcon width={17} height={17} />
            User Entered Price
            <TwoWayInput
              css={css`
                opacity: 1;
                margin-bottom: 0;
              `}
              leftValue={formatPrice(bidPrice, 'BUY')}
              rightValue={formatPrice(askPrice, 'SELL')}
              decimalScale={twoWayPrice.precision}
              isLeftDisabled
              isRightDisabled
            />
          </PriceContainer>
          <div css="display: flex; justify-content: flex-end;">
            <div css="display: flex; align-items: center; justify-content: center; width: 200px; margin: 24px 0;">
              <ArrowDownIcon css="align-self: center;" width={18} height={18} />
            </div>
          </div>
          <PriceContainer>
            <CheckCircleIcon width={20} height={20} />
            Nearest Valid Price
            <TwoWayInput
              css={css`
                opacity: 1;
                border-radius: 3px;
                border: 1px solid ${paradigmLightBlue};
              `}
              leftValue={estimatedBidRounded}
              rightValue={estimatedAskRounded}
              leftHelper={<Helper>{bidPriceDiff}</Helper>}
              rightHelper={<Helper>{askPriceDiff}</Helper>}
              decimalScale={twoWayPrice.precision}
              isLeftDisabled
              isRightDisabled
            />
          </PriceContainer>
          <div css="display: flex; gap: 8px; justify-content: flex-end; margin-top: 48px;">
            <Button ariaLabel="submit quote" onClick={handleSubmitQuote}>
              Accept New Price
            </Button>
            <Button
              ariaLabel="cancel quote"
              variant="link"
              onClick={editLegPrices}
            >
              Reject and Edit Manually
            </Button>
          </div>
        </DarkBlock>
      </Body>
    </HeadedModal>
  );
}

const ONE_BP_PRECISION = 4;
const ONE_BP = BigNumber(10).pow(-ONE_BP_PRECISION);

function formatPriceDiff(priceDiff: BigNumber, side: Side, precision: number) {
  if (priceDiff.isNaN() || priceDiff.isZero()) return '';
  const diffBps = priceDiff.dividedBy(ONE_BP).abs();
  const precisionBp =
    precision >= ONE_BP_PRECISION ? precision - ONE_BP_PRECISION : 0;

  const rounded = roundPrecisionBySide(diffBps.toString(), side, precisionBp);
  const sign = priceDiff.isPositive() ? '+' : '-';
  const unit = diffBps.gt(1) ? 'bps' : 'bp';

  return `${sign}${rounded}${unit}`;
}

const Helper = styled.div`
  position: absolute;
  bottom: -20px;
  left: 50%;
  transform: translateX(-50%);
  font-size: 11px;
  color: ${paradigmLightBlue};
`;

const Body = styled.div`
  padding: 16px;
  padding-top: 12px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const PriceContainer = styled.div`
  display: grid;
  grid-template-columns: 30px 1fr 200px;
  align-items: center;
  width: 100%;
`;

const WarningContainer = styled.div`
  border-radius: 7px;
  font-size: 12px;
  background-color: ${cloudBurst};
  padding: 16px;
  color: rgba(255, 255, 255, 0.7);
`;

const DarkBlock = styled.div`
  padding: 16px;
  background-color: ${darkerBlackPearl};
  border-radius: 5px;

  .mark-container {
    margin-bottom: 16px;
    width: 100%;
    gap: 16px;
    display: flex;
    align-items: center;
    justify-content: flex-end;
  }
`;

const NumberedList = styled.ol`
  padding-inline-start: 24px;
  margin-bottom: 0px;
  li {
    margin-block-start: 8px;
  }
`;
